/* The gallery: the public collections, as the `public_collections` view shows them to anyone
   signed in, and the reports a user makes about one. Copying a collection and reading its
   charts are the collections API's, which already does both for any collection the caller
   can read. docs/specs/2026-09-20-gallery-design.md */

import { z } from "@preflop/core";
import type { SupabaseClient } from "@supabase/supabase-js";
import { DataError, publicCollectionFromRow, type PublicCollection } from "./collections";

export const REPORT_REASONS = [
  ["not-theirs", "Not theirs to share"],
  ["offensive", "Offensive"],
  ["spam", "Spam"],
  ["other", "Something else"],
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number][0];
export const REPORT_NOTE_MAX = 500;
const SEARCH_MAX = 60;

/**
 * A search as it may be sent. It ends up inside the API's filter syntax, where commas,
 * brackets, quotes, colons and backslashes mean something, and `%` and `*` are wildcards: all
 * of those go. What is left is letters, digits, spaces and a little punctuation, which is what
 * a name is made of. It is a filter over rows the caller may read anyway: the worst a clever
 * string can do is find nothing.
 */
export const cleanSearch = (typed: string): string =>
  typed
    .replace(/[^\p{L}\p{N} .+'&-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, SEARCH_MAX);

export interface GalleryQuery {
  search?: string;
  stakes?: string | null;
  format?: string | null;
  sort: "newest" | "copies";
  limit: number;
  /** How many rows the list already holds. */
  offset?: number;
}

export interface GalleryApi {
  list(query: GalleryQuery): Promise<PublicCollection[]>;
  /** One public collection, or null when it is not public (any more). */
  get(id: string): Promise<PublicCollection | null>;
  /** The stakes and the formats found among the public collections, for the two lists. */
  filters(): Promise<{ stakes: string[]; formats: string[] }>;
  /** Report a collection. "already" when this account had: nothing new was written. */
  report(report: {
    collectionId: string;
    reporterId: string;
    reason: ReportReason;
    note: string;
  }): Promise<"sent" | "already">;
  /** The ids of the collections this account has reported. */
  reported(): Promise<Set<string>>;
}

const COLUMNS =
  "id, name, description, spot_count, stakes, formats, published_at, updated_at, owner_name, copies, mine, is_base";
const UNIQUE_VIOLATION = "23505";

const reportSchema = z.object({
  collectionId: z.uuid(),
  reporterId: z.uuid(),
  reason: z.enum(REPORT_REASONS.map(([id]) => id)),
  note: z.string().trim().max(REPORT_NOTE_MAX),
});

export function createGalleryApi(db: SupabaseClient): GalleryApi {
  return {
    async list({ search = "", stakes, format, sort, limit, offset = 0 }) {
      let query = db.from("public_collections").select(COLUMNS);
      const term = cleanSearch(search);
      if (term) {
        // Quoted, so that a space or a dot in the term is part of the value.
        const like = `"*${term}*"`;
        query = query.or(`name.ilike.${like},description.ilike.${like},owner_name.ilike.${like}`);
      }
      if (stakes) query = query.contains("stakes", [stakes]);
      if (format) query = query.contains("formats", [format]);
      // The base first, whatever the order: it is what every other collection started from.
      query = query.order("is_base", { ascending: false });
      query =
        sort === "copies"
          ? query.order("copies", { ascending: false }).order("published_at", { ascending: false })
          : query.order("published_at", { ascending: false });
      // The id last: two collections published in the same instant keep one order across pages.
      const { data, error } = await query.order("id").range(offset, offset + limit - 1);
      if (error) throw new Error(`Could not list the public collections: ${error.message}`);
      return data.map(publicCollectionFromRow);
    },

    async get(id) {
      const { data, error } = await db
        .from("public_collections")
        .select(COLUMNS)
        .eq("id", id)
        .maybeSingle();
      if (error) throw new Error(`Could not read that collection: ${error.message}`);
      return data ? publicCollectionFromRow(data) : null;
    },

    async filters() {
      const { data, error } = await db
        .from("public_collections")
        .select("stakes, formats")
        .limit(1000);
      if (error) throw new Error(`Could not read the gallery's filters: ${error.message}`);
      const rows = z
        .array(z.object({ stakes: z.array(z.string()), formats: z.array(z.string()) }))
        .parse(data);
      const distinct = (pick: (r: (typeof rows)[number]) => string[]): string[] =>
        [...new Set(rows.flatMap(pick))].sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true }),
        );
      return { stakes: distinct((r) => r.stakes), formats: distinct((r) => r.formats) };
    },

    async report(report) {
      const r = reportSchema.parse(report);
      const { error } = await db.from("collection_reports").insert({
        collection_id: r.collectionId,
        reporter_id: r.reporterId,
        reason: r.reason,
        note: r.note,
      });
      if (error?.code === UNIQUE_VIOLATION) return "already";
      if (error) throw new DataError(error.message, error.code);
      return "sent";
    },

    async reported() {
      const { data, error } = await db.from("collection_reports").select("collection_id");
      if (error) throw new Error(`Could not read your reports: ${error.message}`);
      return new Set(
        z
          .array(z.object({ collection_id: z.uuid() }))
          .parse(data)
          .map((r) => r.collection_id),
      );
    },
  };
}
